import { toast } from 'sonner';
import React, { useState } from 'react';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { useAuth } from '../../../../../../hooks/index.js';
import { ActionPrompt } from '../../../components';
import { getStepConfig } from '../../../getStepConfig';
import { determineIfStepIsDisabled } from '../../../utils';
import { usePatientIntakeStatusContext } from '../../../hooks';
import { toggleBaseStepVisibilityWithToast } from '../utils';
import { EnableMHRAccessModal } from './EnableMHRAccessModal';
import type { BaseStepsBaseProps } from '../BaseSteps.types.ts';
import { ViewMHRCompletionModal } from './ViewMHRCompletionModal.tsx';

type MHRModalProps = {
  step: IntakeStatusBaseSteps['MHR_ACCESS'];
  isOpen: boolean;
  onClose: () => void;
};

const MHRModal: React.FC<MHRModalProps> = ({ step, isOpen, onClose }) => {
  const { status } = step.currentStatus;

  if (status === 'COMPLETED') {
    return <ViewMHRCompletionModal step={step} isOpen={isOpen} onClose={onClose} />;
  }

  return <EnableMHRAccessModal step={step} isOpen={isOpen} onClose={onClose} />;
};

interface MHRAccessBaseStepActionProps extends BaseStepsBaseProps {}

export const MHRAccessBaseStepAction: React.FC<MHRAccessBaseStepActionProps> = ({ onEdit }) => {
  const { user, userType } = useAuth();
  const { patientId, baseSteps, isHealthcareProvider } = usePatientIntakeStatusContext();
  const [isMHRModalOpen, setIsMHRModalOpen] = useState(false);

  const step = baseSteps?.MHR_ACCESS;

  const handleOnClick = () => {
    setIsMHRModalOpen(true);
  };

  const handleOnEdit = () => {
    onEdit();
  };

  const handleOnToggleDisabled = async () => {
    if (!step) {
      toast.error('Step not found');

      return;
    }

    if (!user || !userType || !user.uid || !user.email) {
      toast.error('Please login to continue');

      return;
    }

    const isCurrentlyDisabled = determineIfStepIsDisabled({ step });

    await toggleBaseStepVisibilityWithToast({
      step,
      patientId,
      isCurrentlyDisabled,
      stepId: 'MHR_ACCESS',
      context: 'MHR access',
      actionedBy: {
        id: user.uid,
        email: user.email,
        name: null,
        userType,
      },
    }).unwrap();
  };

  if (!step) {
    return null;
  }

  const stepConfig = getStepConfig('MHR_ACCESS');

  const title = stepConfig.title({
    step,
    isHealthcareProvider,
  });

  const description = stepConfig.statuses[step.currentStatus.status].description({
    step,
    isHealthcareProvider,
  });

  return (
    <>
      <ActionPrompt
        step={step}
        title={title}
        description={description}
        onClick={handleOnClick}
        onEdit={handleOnEdit}
        onToggleDisabled={handleOnToggleDisabled}
      />

      {isMHRModalOpen && <MHRModal step={step} isOpen={isMHRModalOpen} onClose={() => setIsMHRModalOpen(false)} />}
    </>
  );
};
